import { useMutation, useQueryClient } from "@tanstack/react-query";
import { VOICES, type ProjectView } from "@joogoos/contracts";
import { api } from "../../api.js";

/**
 * Zone 3 while the chat is on the voice (03).
 *
 * Picking one answers the question outright — narration on, and this is who — while "no narration at all" stays in
 * the chat, where a word is enough. There is no sample to hear yet, so a voice is picked by its name, its accent and
 * a word on how it sounds: a play button that plays nothing says more than the product does (docs/14).
 */
export function Voices({ project }: { project: ProjectView }) {
  const qc = useQueryClient();
  const answer = useMutation({
    mutationFn: (id: string) => api.answer(project.id, { voice: { enabled: true, voiceId: id } }, `${VOICES.find((v) => v.id === id)?.name ?? "That one"}, please`),
    onSuccess: (p) => { qc.setQueryData(["project", p.id], p); void qc.invalidateQueries({ queryKey: ["projects"] }); },
  });
  const line = project.draft.idea ? `${project.draft.idea[0]!.toUpperCase()}${project.draft.idea.slice(1)}.` : "Fresh every Saturday.";

  return (
    <div className="p-6 flex flex-col gap-3.5 h-full min-h-0">
      <div className="flex items-baseline gap-3">
        <h1 className="text-lg font-semibold">Who says it?</h1>
        <span className="text-[13px] text-muted">you hear the voice in the finished video</span>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="grid grid-cols-[repeat(auto-fill,minmax(228px,1fr))] gap-2.5">
          {VOICES.map((v) => (
            <div key={v.id} className="bg-panel border border-line rounded-[10px] p-3 flex flex-col gap-2.5">
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold">{v.name}</div>
                <div className="text-[11.5px] text-muted">{v.accent} · {v.note}</div>
              </div>
              <p className="text-[11.5px] text-muted leading-snug line-clamp-2">“{line}”</p>
              <button
                onClick={() => answer.mutate(v.id)}
                disabled={answer.isPending}
                className="mt-auto py-1.5 rounded-lg bg-coral text-white text-xs font-semibold hover:bg-coral-2 disabled:opacity-40"
              >
                Use {v.name}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
